import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Inbox, ChevronDown, Mail, Phone, Search, Trash2, Download,
} from "lucide-react";
import { accessApi } from "@/access/api";

/* Submissions · raw log of every single form submission across the site.
   Filter by form type. Read-only payload + attribution + page source. */

const FORM_LABELS = {
  all:                "All forms",
  lead:               "Lead / demo",
  build_os:           "Build OS",
  audit:              "Clinic audit",
  integration_notify: "EMR notify",
  newsletter:         "Newsletter",
  support:            "Support",
};

const formAccent = {
  lead:               "bg-violet-500/15 text-violet-200 border-violet-500/25",
  build_os:           "bg-blue-500/15 text-blue-200 border-blue-500/25",
  audit:              "bg-amber-500/15 text-amber-200 border-amber-500/25",
  integration_notify: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/25",
  newsletter:         "bg-sky-500/15 text-sky-200 border-sky-500/25",
  support:            "bg-emerald-500/15 text-emerald-200 border-emerald-500/25",
};

export default function AccessSubmissions() {
  const [params, setParams] = useSearchParams();
  const filter = params.get("form") || "all";
  const setFilter = (key) => {
    if (key === "all") setParams({});
    else setParams({ form: key });
  };
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_form: {} });
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = () => {
    accessApi.submissions(filter === "all" ? {} : { form_type: filter }).then(({ data }) => setRows(data));
    accessApi.submissionCounts().then(({ data }) => setCounts(data));
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      JSON.stringify({ d: r.data, a: r.attribution }).toLowerCase().includes(s)
    );
  }, [rows, q]);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently? This cannot be undone.")) return;
    await accessApi.deleteSubmission(id);
    load();
  };

  const exportCsv = () => {
    const headers = ["id", "form_type", "created_at", "status", "email", "name", "phone", "page", "utm_source", "utm_medium", "utm_campaign", "city", "country", "device", "browser"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const d = r.data || {}; const a = r.attribution || {};
      lines.push([
        r.id, r.form_type, r.created_at, r.status,
        d.email, d.name, d.phone,
        a.page, a.utm_source, a.utm_medium, a.utm_campaign,
        a.city, a.country, a.device, a.browser,
      ].map(escape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `submissions-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="access-submissions" className="space-y-6">
      <header className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-300">Raw log</p>
            <h1 className="mt-1.5 font-display text-3xl font-black tracking-tight">Submissions.</h1>
            <p className="mt-1 text-[13px] text-white/55">
              Every single form submission, captured. Filter by form to verify no data is being lost.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search payload / attribution…"
                data-testid="submissions-search"
                className="rounded-lg border border-white/15 bg-white/[0.04] py-1.5 pl-8 pr-3 text-[12px] outline-none placeholder:text-white/30 focus:border-violet-400/60"
              />
            </div>
            <button
              onClick={exportCsv}
              data-testid="submissions-export"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-1.5 text-[12px] font-bold hover:bg-white/[0.08]"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
          </div>
        </div>

        {/* Form filter chips with counts */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(FORM_LABELS).map(([key, label]) => {
            const n = key === "all" ? counts.total : (counts.by_form?.[key] || 0);
            const active = filter === key;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`submissions-filter-${key}`}
                className={`group inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-bold transition-all ${
                  active
                    ? "border-violet-400/50 bg-violet-500/15 text-violet-100"
                    : "border-white/10 bg-white/[0.03] text-white/65 hover:bg-white/[0.06] hover:text-white"
                }`}
              >
                {label}
                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                  active ? "bg-violet-500/30 text-white" : "bg-white/[0.06] text-white/65"
                }`}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      </header>

      {/* Table — wraps in horizontal scroll on mobile so columns stay readable */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-[13px]">
          <thead className="border-b border-white/10 bg-white/[0.02]">
            <tr>
              <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">Form</th>
              <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">Submitted</th>
              <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">Contact</th>
              <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">Page · source</th>
              <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">UTM</th>
              <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">Geo / device</th>
              <th className="w-16 px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-[12px] text-white/40">
                  <Inbox className="mx-auto mb-2 h-5 w-5" />
                  No submissions {filter !== "all" ? `for ${FORM_LABELS[filter]}` : "yet"}.
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const d = r.data || {}; const a = r.attribution || {};
              const isOpen = !!expanded[r.id];
              return (
                <Fragment key={r.id}>
                  <tr
                    data-testid={`submissions-row-${r.id}`}
                    className="border-b border-white/[0.06] last:border-0 align-top transition-colors hover:bg-white/[0.03]"
                  >
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${formAccent[r.form_type] || "bg-white/10 text-white/70 border-white/15"}`}>
                        {r.form_type}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-mono text-[11.5px] text-white/65">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-bold leading-tight">{d.name || "—"}</p>
                      <div className="mt-1 flex flex-col gap-0.5 text-[11.5px] text-white/65">
                        {d.email && (
                          <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1 hover:text-white">
                            <Mail className="h-3 w-3" /> {d.email}
                          </a>
                        )}
                        {d.phone && (
                          <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1 hover:text-white">
                            <Phone className="h-3 w-3" /> {d.phone}
                          </a>
                        )}
                      </div>
                      {/* Build-OS structured summary — only renders for build_os submissions */}
                      {r.form_type === "build_os" && (
                        <div className="mt-2 space-y-1 rounded-md border border-blue-500/25 bg-blue-500/[0.06] p-1.5 text-[11px]">
                          <p>
                            <span className="text-blue-300 font-bold">{d.selected_base || "—"}</span>
                            {typeof d.selected_base_price === "number" && (
                              <span className="text-white/60"> · ${d.selected_base_price}/mo</span>
                            )}
                          </p>
                          {Array.isArray(d.selected_addons) && d.selected_addons.length > 0 && (
                            <p className="text-white/70">
                              <span className="text-white/40">+ </span>
                              {d.selected_addons.join(", ")}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-[10.5px]">
                            {d.whitelabel && (
                              <span className="inline-flex items-center rounded bg-fuchsia-500/15 px-1.5 py-0.5 font-bold uppercase tracking-[0.14em] text-fuchsia-200">
                                Whitelabel
                              </span>
                            )}
                            {typeof d.total_monthly === "number" && (
                              <span className="font-bold text-white">${d.total_monthly}/mo total</span>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[12px]">
                      {a.page && <p className="font-mono text-white/85">{a.page}</p>}
                      {a.landing_page && a.landing_page !== a.page && (
                        <p className="mt-0.5 font-mono text-[10.5px] text-white/40">landed: {a.landing_page}</p>
                      )}
                      {a.referrer && <p className="mt-0.5 truncate text-[10.5px] text-white/40">ref: {a.referrer}</p>}
                    </td>
                    <td className="px-3 py-3 text-[11.5px]">
                      {a.utm_source ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-violet-200">{a.utm_source}</span>
                          {a.utm_medium && <span className="text-white/55">{a.utm_medium}</span>}
                          {a.utm_campaign && <span className="text-white/40">{a.utm_campaign}</span>}
                        </div>
                      ) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-3 py-3 text-[11.5px] text-white/65">
                      {(a.city || a.country) && (
                        <p>{[a.city, a.country].filter(Boolean).join(", ")}</p>
                      )}
                      <p className="text-[10.5px] text-white/40">
                        {a.device || "—"} · {a.browser || "—"}
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => setExpanded((e) => ({ ...e, [r.id]: !isOpen }))}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 text-white/55 hover:bg-white/[0.06]"
                        >
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          data-testid={`submissions-delete-${r.id}`}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-rose-500/25 bg-rose-500/[0.08] text-rose-200 hover:bg-rose-500/[0.18]"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-white/[0.06] bg-black/30">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="grid gap-4 lg:grid-cols-2">
                          <Kv title="Data" obj={d} />
                          <Kv title="Attribution" obj={a} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

const Kv = ({ title, obj }) => {
  const entries = Object.entries(obj || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!entries.length) return null;
  return (
    <div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">{title}</p>
      <dl className="rounded-md bg-white/[0.02] p-2 font-mono text-[11px]">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-start gap-2 py-0.5">
            <dt className="w-28 flex-none text-white/40">{k}</dt>
            <dd className="min-w-0 flex-1 break-all text-white/85">
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
